import React from 'react';
import { useIntl } from 'react-intl';
import type { SlotChannelGroup } from 'fym-shared/src/music24/oneOffPublish';
import { TypedMemo } from '../../../../common/types';
import { downloadMediaUrl } from '../../inlineMediaUrl';
import { MANUAL_SET_CHANNELS } from './kitManualChannels';
import { KitSlotView, SlotChannel } from './kitSlots';
import { kitMessages } from './kitMessages';
import { publishMessages } from '../../publish/publishMessages';
import { addedAssetsMessages } from '../../addedAssets/addedAssetsMessages';
import { sourceOfAsset } from '../../addedAssets/addedAssetFacts';
import { KitActions } from './useKitActions';
import { WatermarkDownloadLink } from '../../../watermarkUpsell/choice/WatermarkDownloadLink';
import { choiceFileOfKitAsset } from '../../../watermarkUpsell/files/choiceFiles';
import { fileHasWatermark } from '../../../watermarkUpsell/files/watermarkedFile';
/* eslint-disable css-modules/no-unused-class -- a shared stylesheet; each part uses its own slice of it */
import styles from './Kit.module.scss';
import { trackAssetDownloaded } from '../../assetEvents';
/* eslint-enable css-modules/no-unused-class */

export type { SlotChannel } from './kitSlots';

interface KitSlotFootProps {
  view: KitSlotView;
  channels: SlotChannel[];
  /** One group per channel of the slot: a manual mark acts on the group (all files of the slot). */
  groups: ReadonlyArray<SlotChannelGroup<SlotChannel>>;
  actions: KitActions;
  onOpen: () => void;
  onPublish: (entry: SlotChannel) => void;
  /** "I set it" / "I added it" of a channel: one file opens its mark, several files the chooser. */
  onMark: (group: SlotChannelGroup<SlotChannel>) => void;
  /** "Add yours" (design music-video a1, a7): the lyric video, the visualizer and the Canvas. Absent: none. */
  onAddYours?: () => void;
  /** "Remove" of an added version. Absent: the ready version is not added. */
  onRemove?: () => void;
  /** The slot name and the song title: the choice of a watermarked download names them (trial-upsell a2). */
  name?: string;
  songTitle?: string;
}

/** The small "Add yours" link of a slot (design music-video a7). */
const AddYoursLink: React.FC<{ onClick: () => void }> = ({ onClick }) => {
  const intl = useIntl();
  return (
    <button type="button" className={styles.link} onClick={onClick} data-testid="kit-add-yours">
      {intl.formatMessage(addedAssetsMessages.addYours)}
    </button>
  );
};

/**
 * The actions of a slot (design assets-rethink b1 `.ks-f`): "Make it · N credits", "Try again", the
 * manual marks "I added it" and "How?" (Canvas, motion artwork) or "I set it" (banners, thumbnail),
 * "Publish" (a long video), "Download" or "Download N", "Applied by itself" (the link preview), and
 * "See Pro" / "Ask the owner to upgrade." for a locked slot (d3). On Free and the trial, Download of
 * a watermarked file opens the choice first (design: trial-upsell a2).
 */
const KitSlotFootComponent: React.FC<KitSlotFootProps> = (props) => {
  const { view, channels, groups, actions, onOpen, onPublish, onMark, onAddYours, onRemove, name, songTitle } = props;
  const intl = useIntl();
  const { state } = view;
  if (state === 'making') {
    return null;
  }
  if (state === 'locked') {
    return (
      <div className={styles.foot}>
        {actions.isOwner ? (
          <button type="button" className={styles.link} onClick={actions.offerPlan} disabled={!actions.canAct}>
            {intl.formatMessage(kitMessages.seePro)}
          </button>
        ) : (
          <span className={styles.line}>{intl.formatMessage(kitMessages.askOwner)}</span>
        )}
        <span className={styles.grow} />
        {onAddYours !== undefined && actions.canAct && <AddYoursLink onClick={onAddYours} />}
      </div>
    );
  }
  if (state === 'notMade' || state === 'failed') {
    const message = state === 'failed' ? kitMessages.tryAgainCost : kitMessages.makeItCost;
    const label =
      view.credits > 0
        ? intl.formatMessage(message, { cost: view.credits })
        : intl.formatMessage(state === 'failed' ? kitMessages.tryAgain : kitMessages.makeIt);
    return actions.canAct ? (
      <div className={styles.foot}>
        <button
          type="button"
          className={styles.link}
          disabled={actions.isMaking}
          onClick={() => (state === 'failed' ? actions.tryAgain(view) : actions.makeSlot(view))}
        >
          {label}
        </button>
        <span className={styles.grow} />
        {onAddYours !== undefined && <AddYoursLink onClick={onAddYours} />}
      </div>
    ) : null;
  }
  if (view.slot.id === 'linkPreview') {
    return (
      <div className={styles.foot}>
        <span className={styles.line}>{intl.formatMessage(kitMessages.appliedByItself)}</span>
      </div>
    );
  }
  // A channel with a file that is not marked yet. With several files the click opens the chooser.
  const unmarked = groups.find((group) => MANUAL_SET_CHANNELS.includes(group.channel) && group.marked < group.total);
  const isAdd = unmarked !== undefined && (unmarked.channel === 'spotifyCanvas' || unmarked.channel === 'appleMusic');
  // A linked YouTube video is on YouTube already: it has no file to publish again (spec §3.1).
  const isLinked = sourceOfAsset(view.readyAsset) === 'youtube';
  const publishable = view.slot.group === 'longVideos' && !isLinked ? channels[0] : undefined;
  const files = view.readyAssets.filter((asset) => asset.fileUrl !== null);
  const single = files.length === 1 ? files[0] : undefined;
  return (
    <div className={styles.foot}>
      {actions.canAct && unmarked !== undefined && (
        <button type="button" className={styles.link} onClick={() => onMark(unmarked)}>
          {intl.formatMessage(isAdd ? publishMessages.actionAddedIt : publishMessages.actionSetIt)}
        </button>
      )}
      {actions.canAct && isAdd && unmarked !== undefined && (
        <button type="button" className={styles.link} onClick={() => onMark(unmarked)}>
          {intl.formatMessage(kitMessages.howTo)}
        </button>
      )}
      {actions.canAct && publishable !== undefined && (
        <button type="button" className={styles.link} onClick={() => onPublish(publishable)}>
          {intl.formatMessage(kitMessages.publish)}
        </button>
      )}
      <span className={styles.grow} />
      {actions.canAct && onRemove !== undefined && (
        <button type="button" className={styles.link} onClick={onRemove}>
          {intl.formatMessage(addedAssetsMessages.remove)}
        </button>
      )}
      {single !== undefined && single.fileUrl !== null ? (
        <WatermarkDownloadLink
          className={styles.link}
          href={downloadMediaUrl(single.fileUrl)}
          hasWatermark={fileHasWatermark(single)}
          file={choiceFileOfKitAsset(single, name ?? null, songTitle ?? null)}
          onDownload={() => trackAssetDownloaded(single.themeId, 'kit')}
        >
          {intl.formatMessage(kitMessages.download)}
        </WatermarkDownloadLink>
      ) : (
        files.length > 1 && (
          <button type="button" className={styles.link} onClick={onOpen}>
            {intl.formatMessage(kitMessages.downloadCount, { count: files.length })}
          </button>
        )
      )}
    </div>
  );
};

export const KitSlotFoot = TypedMemo(KitSlotFootComponent);
